import { create } from "zustand";
import {
  definePersistedKey,
  getPersistedStorageKey,
  inspectPersistedValue,
  removePersistedValue,
  writePersistedValue,
} from "../utils/persistedStorage";
import { removeSafeStorage } from "../utils/safeStorage";
import {
  ensurePersistedLoginInstance,
  readPersistedLoginInstance,
} from "./sessionPersistence";

type ActiveLinePreferenceValidation = "active" | "pending";

/**
 * A caller-ID choice and the line-list response that is allowed to validate
 * it. Replacements always receive a new id so a late refusal cannot clear a
 * same-number choice made later in this tab.
 */
export type PhoneActiveLinePreference = Readonly<{
  id: string;
  loginInstance: string;
  phoneNumber: string;
  authoritativeFrom: number;
  validation: ActiveLinePreferenceValidation;
}>;

export type PhoneActiveLinePreferences = Record<
  string,
  PhoneActiveLinePreference
>;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

type ParsedPhoneActiveLinePreference = {
  id: unknown;
  loginInstance: unknown;
  phoneNumber: unknown;
  authoritativeFrom: unknown;
  validation: unknown;
};

const isTimestamp = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const isPhoneActiveLinePreference = (
  value: unknown,
): value is PhoneActiveLinePreference => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as ParsedPhoneActiveLinePreference;
  return (
    isNonEmptyString(record.id) &&
    isNonEmptyString(record.loginInstance) &&
    isNonEmptyString(record.phoneNumber) &&
    isTimestamp(record.authoritativeFrom) &&
    (record.validation === "active" || record.validation === "pending")
  );
};

const isPhoneActiveLinePreferences = (
  value: unknown,
): value is PhoneActiveLinePreferences => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  return Object.entries(value).every(
    ([accountId, preference]) =>
      accountId.length > 0 && isPhoneActiveLinePreference(preference),
  );
};

const activeLinePreferences = definePersistedKey<
  PhoneActiveLinePreferences,
  "user"
>({
  owner: "phone",
  name: "active-lines",
  scope: "user",
  storage: "session",
  version: 2,
  parse: (rawValue) => {
    const value: unknown = JSON.parse(rawValue);
    return isPhoneActiveLinePreferences(value) ? value : null;
  },
  serialize: JSON.stringify,
});

// v1 used localStorage and contained bare account -> number pairs. It is
// deliberately never migrated: existing authenticated sessions have no login
// instance, and carrying their old preference forward could resurrect it after
// a missed logout. Remove it when this feature is next read or cleared.
const legacyActiveLinePreferences = definePersistedKey<string, "user">({
  owner: "phone",
  name: "active-lines",
  scope: "user",
  storage: "local",
  version: 1,
  parse: () => null,
  serialize: (value) => value,
});

const preferenceCacheByUser = new Map<string, PhoneActiveLinePreferences>();

const preferenceId = (): string => {
  try {
    return globalThis.crypto?.randomUUID() ?? `${Date.now()}-${Math.random()}`;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
};

const legacyPreferencesKey = (userId: string): string =>
  getPersistedStorageKey(legacyActiveLinePreferences, { userId });

const removeLegacyPreferences = (userId: string): void => {
  removeSafeStorage(legacyPreferencesKey(userId), "local");
};

const removeStoredPreferences = (userId: string): void => {
  removePersistedValue(activeLinePreferences, { userId });
  removeLegacyPreferences(userId);
};

const hasCurrentLoginInstance = (
  preferences: PhoneActiveLinePreferences,
  loginInstance: string,
): boolean =>
  Object.values(preferences).every(
    (preference) => preference.loginInstance === loginInstance,
  );

const hydratePreferences = (
  preferences: PhoneActiveLinePreferences,
  loginInstance: string,
): PhoneActiveLinePreferences =>
  Object.fromEntries(
    Object.entries(preferences)
      .filter(([, preference]) => preference.loginInstance === loginInstance)
      .map(([accountId, preference]) => [
        accountId,
        {
          ...preference,
          id: preferenceId(),
          loginInstance,
          authoritativeFrom: Date.now(),
          validation: "pending" as const,
        },
      ]),
  );

/**
 * Read this tab's records. A refresh or runtime reset re-hydrates every
 * surviving choice as a new pending identity, so an old query response cannot
 * authorize its removal or a late 422 cannot clear its replacement.
 */
const preferencesForUser = (userId: string): PhoneActiveLinePreferences => {
  const login = ensurePersistedLoginInstance();
  if (!login) {
    preferenceCacheByUser.delete(userId);
    removeStoredPreferences(userId);
    return {};
  }

  if (login.bootstrapped) {
    preferenceCacheByUser.delete(userId);
    removeStoredPreferences(userId);
    return {};
  }

  const { value: loginInstance } = login;

  const cached = preferenceCacheByUser.get(userId);
  if (cached && hasCurrentLoginInstance(cached, loginInstance)) return cached;

  preferenceCacheByUser.delete(userId);
  removeLegacyPreferences(userId);
  const inspected = inspectPersistedValue(activeLinePreferences, { userId });
  if (inspected.state === "invalid") {
    removePersistedValue(activeLinePreferences, { userId });
    return {};
  }
  if (inspected.state !== "value") return {};

  const hydrated = hydratePreferences(inspected.value, loginInstance);
  if (Object.keys(hydrated).length === 0) {
    removePersistedValue(activeLinePreferences, { userId });
    return {};
  }
  writePersistedValue(activeLinePreferences, hydrated, { userId });
  preferenceCacheByUser.set(userId, hydrated);
  return hydrated;
};

const persistPreferences = (
  userId: string,
  preferences: PhoneActiveLinePreferences,
): boolean => {
  return Object.keys(preferences).length === 0
    ? removePersistedValue(activeLinePreferences, { userId })
    : writePersistedValue(activeLinePreferences, preferences, { userId });
};

/** Commit a user-selected caller ID only after its durable write succeeds. */
const persistAndCachePreferences = (
  userId: string,
  preferences: PhoneActiveLinePreferences,
): boolean => {
  const persisted = persistPreferences(userId, preferences);
  if (persisted) {
    preferenceCacheByUser.set(userId, preferences);
  }
  return persisted;
};

const persistValidationMetadata = (
  userId: string,
  preferences: PhoneActiveLinePreferences,
): void => {
  persistPreferences(userId, preferences);
  // A fresh line list has already confirmed this same choice. Keep that
  // in-memory watermark even if sessionStorage is temporarily unavailable so
  // an older response cannot clear it before the next retry.
  preferenceCacheByUser.set(userId, preferences);
};

const withPreference = (
  preferences: PhoneActiveLinePreferences,
  accountId: string,
  preference: PhoneActiveLinePreference | null,
) => {
  if (preference !== null) {
    return { ...preferences, [accountId]: preference };
  }
  const { [accountId]: _removed, ...remaining } = preferences;
  return remaining;
};

type ActiveLineStore = {
  revision: number;
  selectLine: (
    userId: string,
    accountId: string,
    phoneNumber: string | null,
    validatedLineListUpdatedAt?: number,
  ) => boolean;
  clearLineIfSelected: (
    userId: string,
    accountId: string,
    expectedPreferenceId: string,
  ) => boolean;
  reconcileLine: (
    userId: string,
    accountId: string,
    lineListUpdatedAt: number,
    containsSelectedLine: boolean,
  ) => ActiveLineReconciliation;
  clearUser: (userId: string) => void;
  reset: () => void;
};

export type ActiveLineReconciliation =
  | "active"
  | "cleared"
  | "none"
  | "refresh";

export const useActiveLineStore = create<ActiveLineStore>((set, get) => ({
  revision: 0,
  selectLine: (userId, accountId, phoneNumber, validatedLineListUpdatedAt) => {
    let persisted = false;
    set((state) => {
      const preferences = preferencesForUser(userId);
      const loginInstance = readPersistedLoginInstance();
      if (!loginInstance) {
        return { revision: state.revision + 1 };
      }
      const hasValidatedLineList = isTimestamp(validatedLineListUpdatedAt);
      const preference = phoneNumber
        ? {
            id: preferenceId(),
            loginInstance,
            phoneNumber,
            authoritativeFrom: hasValidatedLineList
              ? validatedLineListUpdatedAt
              : Date.now(),
            validation: hasValidatedLineList
              ? ("active" as const)
              : ("pending" as const),
          }
        : null;
      const next = withPreference(preferences, accountId, preference);
      persisted = persistAndCachePreferences(userId, next);
      return { revision: state.revision + 1 };
    });
    return persisted;
  },
  clearLineIfSelected: (userId, accountId, expectedPreferenceId) => {
    let cleared = false;
    set((state) => {
      const preferences = preferencesForUser(userId);
      if (preferences[accountId]?.id !== expectedPreferenceId) return state;
      const next = withPreference(preferences, accountId, null);
      cleared = persistAndCachePreferences(userId, next);
      return cleared ? { revision: state.revision + 1 } : state;
    });
    return cleared;
  },
  reconcileLine: (
    userId,
    accountId,
    lineListUpdatedAt,
    containsSelectedLine,
  ) => {
    const preference = activeLinePreferenceForAccount(userId, accountId);
    if (!preference) return "none";
    if (
      activeLinePreferenceNeedsLineRefresh(userId, accountId, lineListUpdatedAt)
    ) {
      return "refresh";
    }
    if (!containsSelectedLine) {
      const cleared = get().clearLineIfSelected(
        userId,
        accountId,
        preference.id,
      );
      return cleared ? "cleared" : "active";
    }
    if (
      preference.validation === "pending" ||
      lineListUpdatedAt > preference.authoritativeFrom
    ) {
      set((state) => {
        const preferences = preferencesForUser(userId);
        if (preferences[accountId]?.id !== preference.id) return state;
        const next = withPreference(preferences, accountId, {
          ...preference,
          authoritativeFrom: lineListUpdatedAt,
          validation: "active",
        });
        persistValidationMetadata(userId, next);
        return { revision: state.revision + 1 };
      });
    }
    return "active";
  },
  clearUser: (userId) => {
    preferenceCacheByUser.delete(userId);
    removeStoredPreferences(userId);
    set((state) => ({ revision: state.revision + 1 }));
  },
  reset: () => {
    preferenceCacheByUser.clear();
    set((state) => ({ revision: state.revision + 1 }));
  },
}));

/** The current per-tab override record, or null when the seat follows default. */
export const activeLinePreferenceForAccount = (
  userId: string | null,
  accountId: string | null,
): PhoneActiveLinePreference | null => {
  if (!userId || !accountId) return null;
  return preferencesForUser(userId)[accountId] ?? null;
};

/** The chosen override for a user's account, or null (= seat default). */
export const selectedLineForAccount = (
  userId: string | null,
  accountId: string | null,
): string | null =>
  activeLinePreferenceForAccount(userId, accountId)?.phoneNumber ?? null;

/**
 * A pending restored record needs a strictly newer list; an active picker
 * record may use the same rendered list that created it.
 */
export const activeLinePreferenceNeedsLineRefresh = (
  userId: string | null,
  accountId: string | null,
  lineListUpdatedAt: number,
): boolean => {
  const preference = activeLinePreferenceForAccount(userId, accountId);
  if (!preference || !Number.isFinite(lineListUpdatedAt)) return false;
  return preference.validation === "pending"
    ? lineListUpdatedAt <= preference.authoritativeFrom
    : lineListUpdatedAt < preference.authoritativeFrom;
};

/** Reconcile only in the provider that owns the currently rendered line list. */
export const reconcileActiveLinePreference = (
  userId: string | null,
  accountId: string | null,
  lineListUpdatedAt: number,
  containsSelectedLine: boolean,
): ActiveLineReconciliation => {
  if (!userId || !accountId) return "none";
  return useActiveLineStore
    .getState()
    .reconcileLine(userId, accountId, lineListUpdatedAt, containsSelectedLine);
};

/** Clear one tab's session-scoped overrides at an auth boundary. */
export const clearActiveLinePreferences = (userId: string | null): void => {
  if (userId) useActiveLineStore.getState().clearUser(userId);
};

/** Routine runtime teardown drops only this tab's warmed values. */
export const resetActiveLine = (): void =>
  useActiveLineStore.getState().reset();
